/**
 * Wall edits: breaks (F2), wall drag with connector insertion (F5), zero-length
 * cleanup, room drag with snapping (F10), opening drag (F8).
 */
import { CENTER_SNAP_FT, POLICY, SAME_TOL_FT } from './policy';
import { edgeSegments, segmentAt } from './segments';
import { splitMissingAtJunctions } from './wallMeetsOpening';
import { copyWall, moveWall, wallFtOf } from './thickness';
import { reconcileShared } from './walls';
import {
  EPS, area, axisOf, bbox, clamp, clone, dist, edgeOf, findRoom, outN, projOff, round6, snapIn, tidy, unit, vIdx, MIN_POLY_CORNERS,
} from './geom';
import { buildUF, compOf, isMeasured } from './uf';
import { isPinned, openGeom, sharedMap, syncLinked } from './shared';
import { uid } from './plan';
import { doorDefault, doorSideOf } from './openings';
import type { Axis, Corner, Fail, Measurement, Plan, Pt, Room } from './types';

const repointOpen = (r: Room, from: number, to: number) => {
  r.openings.forEach((o) => { if (o.edgeStart === from) o.edgeStart = to; });
};

/**
 * Merge zero-length walls. Returns removed-corner -> kept-corner ids so the
 * screen can repoint its selection.
 */
export function cleanupZero(r: Room): Map<number, number> {
  const merged = new Map<number, number>();
  let again = true;
  while (again && r.corners.length > MIN_POLY_CORNERS) {
    again = false;
    const n = r.corners.length;
    for (let i = 0; i < n; i++) {
      const a = r.corners[i], j = (i + 1) % n, b = r.corners[j];
      if (dist(a, b) < 1e-6) {
        r.corners.splice(j, 1);
        repointOpen(r, b.id, a.id);
        moveWall(r, b.id, a.id); // wall b->c is now a->c
        r.measurements.forEach((m) => { if (m.a === b.id) m.a = a.id; if (m.b === b.id) m.b = a.id; });
        r.measurements = r.measurements.filter((m) => m.a !== m.b);
        merged.forEach((to, from) => { if (to === b.id) merged.set(from, a.id); });
        merged.set(b.id, a.id);
        again = true;
        break;
      }
    }
  }
  return merged;
}

/**
 * Insert break corners on wall `vid` at the given offsets (snapped, at least
 * the break clearance from either end). Openings on the wall keep their world
 * position by moving to the sub-wall they fall on.
 */
export function insertBreaks(plan: Plan, r: Room, vid: number, offsIn: number[]): Corner[] {
  const E = edgeOf(r, vid);
  if (!E) return [];
  const L = dist(E.a, E.b), u = unit(E.a, E.b), c = POLICY.break_clearance_ft;
  const offs = [...new Set(offsIn.map(snapIn))].filter((o) => o >= c && o <= L - c).sort((a, b) => a - b);
  if (!offs.length) return [];
  const nv = offs.map((o) => ({ id: uid(plan), x: E.a.x + u.x * o, y: E.a.y + u.y * o }));
  r.corners.splice(E.i + 1, 0, ...nv);
  nv.forEach((v) => copyWall(r, E.a.id, v.id)); // each piece keeps the wall's thickness
  r.openings.filter((o) => o.edgeStart === E.a.id).forEach((o) => {
    let st = 0, sid = E.a.id;
    offs.forEach((b, k) => { if (o.offFt >= b - 1e-6) { sid = nv[k].id; st = b; } });
    o.edgeStart = sid;
    o.offFt -= st;
  });
  return nv;
}

/**
 * Long-press break (F2). One offset adds one break; a second offset at least
 * `bump_min_ft` away adds two (a bump-out). Returns the wall to select.
 */
export function addBreak(
  plan: Plan, rid: string, vid: number, a: number, b?: number | null,
): { ok: true; vid: number; count: number } | Fail<'too-close' | 'missing'> {
  const r = findRoom(plan, rid);
  if (!r || vIdx(r, vid) < 0) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const two = b != null && Math.abs(b - a) >= POLICY.bump_min_ft;
  const nv = insertBreaks(plan, r, vid, two ? [a, b!] : [a]);
  if (!nv.length) return { ok: false, err: 'too-close', message: 'Too close to a corner for a break' };
  tidy(plan);
  return { ok: true, vid: nv[0].id, count: nv.length };
}

export interface WallDrag {
  kind: 'wall'; rid: string; vid: number; ax: Axis; vids: number[]; start: number[];
  /** FIELD-4 K8: a measured wall may be dragged; set on release, the measurements the drag changed (now rough). */
  rough?: Measurement[];
}

/**
 * Start dragging wall `vid` of a selected room. Collinear neighbours get a
 * zero-length connector corner so only this wall moves. Refused (with the
 * prototype's message, and no change) on a shared or measured wall. On ok the
 * plan already holds the connectors; `vid` is the dragged wall's start corner.
 */
export function beginWallDrag(
  plan: Plan, rid: string, vid: number, opts: { allowMeasured?: boolean } = {},
): { ok: true; drag: WallDrag; vid: number } | Fail<'not-straight' | 'shared' | 'measured' | 'missing'> {
  const room = findRoom(plan, rid);
  if (!room || vIdx(room, vid) < 0) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const r = clone(room);
  let i = vIdx(r, vid), n = r.corners.length;
  let a = r.corners[i];
  const t = axisOf(a, r.corners[(i + 1) % n]);
  if (t !== 'h' && t !== 'v') return { ok: false, err: 'not-straight', message: 'Only straight walls can be dragged' };
  const ax: Axis = t === 'h' ? 'y' : 'x';
  let next = plan.next;
  const prev = r.corners[(i - 1 + n) % n];
  if (axisOf(prev, a) === t) {
    const a2 = { id: next++, x: a.x, y: a.y };
    r.corners.splice(i + 1, 0, a2);
    repointOpen(r, a.id, a2.id);
    copyWall(r, a.id, a2.id); // the connector and the dragged wall keep its thickness
    i++; n++; a = a2;
  }
  const ib = (i + 1) % n;
  const b = r.corners[ib], nxt = r.corners[(ib + 1) % n];
  if (axisOf(b, nxt) === t) {
    const b2 = { id: next++, x: b.x, y: b.y };
    r.corners.splice(ib + 1, 0, b2);
    repointOpen(r, b.id, b2.id);
    copyWall(r, b.id, b2.id);
  }
  // Test the clone in the plan's place, so shared walls are found against it.
  const idx = plan.rooms.indexOf(room);
  plan.rooms[idx] = r;
  // FIELD-4 K8 (`allowMeasured`): only the wall's own line moves (straight walls, no
  // measurements); the measurements it changes go back to rough on release (`endWallDrag`).
  const U = buildUF(r, !opts.allowMeasured);
  const pinned = isPinned(plan, r, U, ax, a.id);
  const measured = !opts.allowMeasured && isMeasured(r, U, ax, a.id);
  if (pinned || measured) {
    plan.rooms[idx] = room;
    return pinned
      ? { ok: false, err: 'shared', message: 'This wall is set by the room it attaches to. Move the room instead.' }
      : { ok: false, err: 'measured', message: 'This wall is set by a measurement. Use Length or Measure across to change it.' };
  }
  plan.rooms[idx] = room;
  room.corners = r.corners;
  room.openings = r.openings;
  if (r.walls) room.walls = r.walls;
  plan.next = next;
  const vs = compOf(room, U, ax, a.id);
  return { ok: true, vid: a.id, drag: { kind: 'wall', rid, vid: a.id, ax, vids: vs.map((v) => v.id), start: vs.map((v) => v[ax]) } };
}

/** Move the dragged wall by a world delta (snapped along the wall's normal axis). */
export function dragWall(plan: Plan, drag: WallDrag, delta: Pt): void {
  const r = findRoom(plan, drag.rid);
  if (!r) return;
  const d = snapIn(drag.ax === 'x' ? delta.x : delta.y);
  drag.vids.forEach((id, k) => {
    const v = r.corners.find((c) => c.id === id);
    if (v) v[drag.ax] = drag.start[k] + d;
  });
}

/** Release: merge any zero-length walls the drag left; K8: the measurements it changed go rough (`drag.rough`). */
export function endWallDrag(plan: Plan, drag: WallDrag): Map<number, number> {
  const r = findRoom(plan, drag.rid);
  if (r) roughenChanged(r, drag);
  const merged = r ? cleanupZero(r) : new Map<number, number>();
  tidy(plan);
  splitMissingAtJunctions(plan); // FIELD-4 K7
  return merged;
}

/**
 * FIELD-4 K8: a measurement crossing the dragged line whose reading no longer matches
 * the drawing goes back to rough (dropped); one the drag left matching (it never moved,
 * or it landed on a snap that restores it) stays. Sets and returns `drag.rough`.
 */
export function roughenChanged(r: Room, drag: WallDrag): Measurement[] {
  const ids = new Set(drag.vids);
  const at = (id: number) => r.corners.find((c) => c.id === id);
  const rough = r.measurements.filter((m) => {
    if (m.axis !== drag.ax || ids.has(m.a) === ids.has(m.b)) return false;
    const a = at(m.a), b = at(m.b);
    return !!a && !!b && Math.abs(Math.abs(b[m.axis] - a[m.axis]) - m.valueFt) >= SAME_TOL_FT;
  });
  if (rough.length) r.measurements = r.measurements.filter((m) => !rough.includes(m));
  drag.rough = rough;
  return rough;
}

export interface SnapResult {
  x: number | null;
  y: number | null;
  gx: { ax: Axis; val: number; from: Pt; to: Pt } | null;
  gy: { ax: Axis; val: number; from: Pt; to: Pt } | null;
}

/**
 * F10. Axis 1: one wall from a facing wall, at that wall's stored thickness (within
 * snap_flush_ft of that position, so a drop short of it, flush, or a little into the
 * neighbour all land one wall away: founder rule 2026-11-02, B-7564).
 * Axis 2: a corner lines up with a neighbour's corner, within
 * max(snap_min_ft, snap_corner_px / scale) where `scale` is px per foot. A corner
 * snap never wins on an axis that has a one-wall snap, and never moves the room to
 * less than one wall from a facing wall of that neighbour (it aligns along a wall,
 * never pulls two faces together across one).
 */
export function snapRoom(r: Room, others: Room[], scale: number): SnapResult {
  const cornerThr = Math.max(POLICY.snap_min_ft, POLICY.snap_corner_px / scale);
  const best: SnapResult = { x: null, y: null, gx: null, gy: null };
  const take = (ax: Axis, d: number, thr: number, guide: SnapResult['gx']) => {
    const cur = best[ax];
    if (Math.abs(d) < thr && (cur === null || Math.abs(d) < Math.abs(cur))) {
      best[ax] = d;
      if (ax === 'x') best.gx = guide; else best.gy = guide;
    }
  };
  const aR = area(r);
  // Facing wall pairs (overlapping along the wall): r's face at `a`, outward sign `s` on `ax`; B's face at `b`, thickness `T`.
  const faces: { B: Room; ax: Axis; a: number; s: number; b: number; T: number }[] = [];
  r.corners.forEach((a1, i) => {
    const a2 = r.corners[(i + 1) % r.corners.length], t = axisOf(a1, a2);
    if (t !== 'h' && t !== 'v') return;
    const nA = outN(r, a1, a2, aR);
    const lx: Axis = t === 'h' ? 'y' : 'x', ox: Axis = t === 'h' ? 'x' : 'y';
    others.forEach((B) => {
      const aB = area(B);
      B.corners.forEach((b1, j) => {
        const b2 = B.corners[(j + 1) % B.corners.length];
        if (axisOf(b1, b2) !== t) return;
        const nB = outN(B, b1, b2, aB);
        if (nA.x * nB.x + nA.y * nB.y > POLICY.facing_dot_max) return;
        const lo = Math.max(Math.min(a1[ox], a2[ox]), Math.min(b1[ox], b2[ox]));
        const hi = Math.min(Math.max(a1[ox], a2[ox]), Math.max(b1[ox], b2[ox]));
        if (hi - lo <= EPS) return;
        const T = wallFtOf(B, b1.id), s = Math.sign(nA[lx]);
        faces.push({ B, ax: lx, a: a1[lx], s, b: b1[lx], T });
        if (hi - lo < POLICY.snap_min_overlap_ft) return;
        // One wall from it, at the neighbour wall's own stored thickness (FIELD-2 E6).
        take(lx, b1[lx] - s * T - a1[lx], POLICY.snap_flush_ft, null);
      });
    });
  });
  const walled = { x: best.x !== null, y: best.y !== null };
  // Would shifting r by `d` on `ax` leave a facing wall of B less than one wall away (but not past it)?
  const crosses = (B: Room, ax: Axis, d: number) => faces.some((f) => {
    if (f.B !== B || f.ax !== ax) return false;
    const gap = (f.b - f.a - d) * f.s;
    return gap > -f.T - EPS && gap < f.T - EPS;
  });
  const R = bbox(r.corners), reach = POLICY.snap_corner_reach_ft;
  others.forEach((B) => {
    const Q = bbox(B.corners);
    const [rx0, ry0, rx1, ry1] = R, [qx0, qy0, qx1, qy1] = Q;
    if (rx0 > qx1 + reach || qx0 > rx1 + reach || ry0 > qy1 + reach || qy0 > ry1 + reach) return;
    r.corners.forEach((a) => B.corners.forEach((b) => {
      (['x', 'y'] as Axis[]).forEach((ax) => {
        const d = b[ax] - a[ax];
        if (walled[ax] || crosses(B, ax, d)) return;
        take(ax, d, cornerThr, { ax, val: b[ax], from: a, to: b });
      });
    }));
  });
  return best;
}

export interface RoomDrag { kind: 'room'; rid: string; start: Pt[] }

export function beginRoomDrag(plan: Plan, rid: string): { ok: true; drag: RoomDrag } | Fail<'missing'> {
  const r = findRoom(plan, rid);
  if (!r) return { ok: false, err: 'missing', message: 'That room is gone' };
  return { ok: true, drag: { kind: 'room', rid, start: r.corners.map((p) => ({ x: p.x, y: p.y })) } };
}

/**
 * Move the room by a world delta from the drag start, snapping per F10 (or to
 * the inch when nothing is near). Returns guide lines to draw.
 */
export function dragRoom(plan: Plan, drag: RoomDrag, delta: Pt, scale: number): { guides: [Pt, Pt][] } {
  const r = findRoom(plan, drag.rid);
  if (!r) return { guides: [] };
  r.corners.forEach((p, k) => { p.x = drag.start[k].x + delta.x; p.y = drag.start[k].y + delta.y; });
  const sn = snapRoom(r, plan.rooms.filter((q) => q !== r), scale);
  const s0 = drag.start[0];
  const fx = sn.x !== null ? sn.x : snapIn(s0.x + delta.x) - (s0.x + delta.x);
  const fy = sn.y !== null ? sn.y : snapIn(s0.y + delta.y) - (s0.y + delta.y);
  r.corners.forEach((p) => { p.x += fx; p.y += fy; });
  const guides: [Pt, Pt][] = [];
  const over = POLICY.snap_guide_overhang_ft;
  for (const g of [sn.gx, sn.gy]) {
    if (!g) continue;
    const ox: Axis = g.ax === 'x' ? 'y' : 'x';
    const p = { x: 0, y: 0 }, q = { x: 0, y: 0 };
    p[g.ax] = q[g.ax] = g.val;
    p[ox] = Math.min(g.from[ox], g.to[ox]) - over;
    q[ox] = Math.max(g.from[ox], g.to[ox]) + over;
    guides.push([p, q]);
  }
  return { guides };
}

/** Release: tidy, and a new shared wall takes one stored thickness on both sides (FIELD-2 E6). */
export function endRoomDrag(plan: Plan): void { tidy(plan); reconcileShared(plan); splitMissingAtJunctions(plan); }

export interface OpeningDrag {
  kind: 'open'; rid: string; oid: number; mode: 'slide' | 'swing' | null; grab: number; w0: Pt;
  /** FIELD-4 K11: the opening sits centred on its wall segment (the screen ticks once on the way in). */
  centred?: boolean;
}

export function beginOpeningDrag(plan: Plan, rid: string, oid: number, wp: Pt): { ok: true; drag: OpeningDrag } | Fail<'missing'> {
  const r = findRoom(plan, rid);
  const o = r?.openings.find((q) => q.id === oid);
  if (!r || !o) return { ok: false, err: 'missing', message: 'That opening is gone' };
  return { ok: true, drag: { kind: 'open', rid, oid, mode: null, grab: projOff(r, o.edgeStart, wp) - o.offFt, w0: wp } };
}

/**
 * F8: drag along the wall slides; a door dragged toward a side sets swing in or
 * out and the hinge end. The first movement picks the mode. Linked copies follow.
 */
export function dragOpening(plan: Plan, drag: OpeningDrag, wp: Pt): void {
  const r = findRoom(plan, drag.rid);
  const o = r?.openings.find((q) => q.id === drag.oid);
  const E = r && o ? edgeOf(r, o.edgeStart) : null;
  if (!r || !o || !E) return;
  const L = dist(E.a, E.b), u = unit(E.a, E.b), nO = outN(r, E.a, E.b);
  if (!drag.mode) {
    const dx = wp.x - drag.w0.x, dy = wp.y - drag.w0.y;
    const al = Math.abs(dx * u.x + dy * u.y), pe = Math.abs(dx * nO.x + dy * nO.y);
    drag.mode = o.kind === 'door' && pe > al ? 'swing' : 'slide';
  }
  if (drag.mode === 'slide') {
    o.offFt = clamp(projOff(r, o.edgeStart, wp) - drag.grab, 0, Math.max(0, L - o.widthFt));
    // K11: within center_snap_in of its segment's centre (K6), it centres; dragging past releases it.
    const seg = segmentAt(edgeSegments(plan, r.id, o.edgeStart), o.offFt + o.widthFt / 2);
    const c = seg ? (seg.s0 + seg.s1) / 2 : null;
    drag.centred = c != null && seg!.lengthFt >= o.widthFt - EPS && Math.abs(o.offFt + o.widthFt / 2 - c) <= CENTER_SNAP_FT + EPS;
    if (drag.centred) o.offFt = round6(c! - o.widthFt / 2);
  } else {
    const M = sharedMap(plan);
    const g = openGeom(r, o, M);
    const rel = (wp.x - E.a.x) * nO.x + (wp.y - E.a.y) * nO.y;
    const t = (wp.x - E.a.x) * u.x + (wp.y - E.a.y) * u.y;
    const type = o.door?.type ?? doorDefault(plan, doorSideOf(plan, r, o, M)).type;
    o.door = { type, out: rel > (g ? g.thick : 0) / 2, hingeAtEnd: t > o.offFt + o.widthFt / 2 };
  }
  syncLinked(plan, r, o);
}
